// components/CustomDataGrid.jsx
//
// Self-built table (no @mui/x-data-grid) — same pattern as EduTech's
// components/ui/portal-data-table.tsx: a plain table, columns as a simple
// array, built-in search/pagination/loading/empty states — styled to match
// this app's existing light theme instead of Tailwind/dark mode.
//
// Column shape stays identical to what every caller already passes
// ({ field, headerName, width, renderCell }) so existing renderCell
// callbacks (which expect { value, row }) keep working unchanged.
import { useMemo, useState } from "react";
import {
  Box,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TextField,
  Select,
  MenuItem,
  Button,
  Skeleton,
  Typography,
} from "@mui/material";

const PAGE_SIZE_OPTIONS = [5, 10, 15, 50, 100, "All"];
const BORDER_COLOR = "#e0e0e0";
// Columns without an explicit width cap here so long free-text values wrap
// onto multiple lines instead of stretching the table far to the right.
const DEFAULT_COL_MAX_WIDTH = 240;

const CustomDataGrid = ({ rows, columns, setPage, loading = false }) => {
  const [filterText, setFilterText] = useState("");
  const [pageSize, setPageSize] = useState(10);
  // Pagination is always owned internally — some callers pass `setPage` to
  // get notified of page changes (e.g. for their own row numbering), but
  // nothing here reads a `page` prop back. Depending on that echo used to
  // break Next/Previous entirely for any caller that forgot to also pass
  // `page`: currentPage would permanently read as 1, so every "next" click
  // re-requested the same page 2 and silently did nothing after the first.
  const [currentPage, setCurrentPage] = useState(1);

  const goToPage = (nextPage) => {
    setCurrentPage(nextPage);
    setPage?.(nextPage);
  };

  const filteredRows = useMemo(() => {
    if (!filterText.trim()) return rows;
    const lower = filterText.toLowerCase();
    return rows.filter((row) =>
      Object.values(row).some((val) => String(val ?? "").toLowerCase().includes(lower))
    );
  }, [rows, filterText]);

  const totalCount = filteredRows.length;
  const totalPages =
    pageSize === "All" ? 1 : Math.max(1, Math.ceil(totalCount / pageSize));

  const pagedRows = useMemo(() => {
    if (pageSize === "All") return filteredRows;
    const start = (currentPage - 1) * pageSize;
    return filteredRows.slice(start, start + pageSize);
  }, [filteredRows, currentPage, pageSize]);

  const pageOffset = (currentPage - 1) * (pageSize === "All" ? 0 : Number(pageSize));

  const handleFilterChange = (e) => {
    setFilterText(e.target.value);
    goToPage(1);
  };

  const handlePageSizeChange = (e) => {
    const value = e.target.value === "All" ? "All" : Number(e.target.value);
    setPageSize(value);
    goToPage(1);
  };

  const skeletonRowCount = Math.min(pageSize === "All" ? 5 : pageSize, 8);

  return (
    <Box sx={{ width: "100%" }}>
      {/* Search + entries selector */}
      <Box
        sx={{
          mb: 2,
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
        }}
      >
        <TextField
          placeholder="Cari data..."
          size="small"
          value={filterText}
          onChange={handleFilterChange}
          sx={{ minWidth: 260, flex: "1 1 260px", maxWidth: 420 }}
        />

        <Box sx={{ display: "flex", alignItems: "center", gap: 1, whiteSpace: "nowrap" }}>
          <Typography variant="body2" color="text.secondary">
            Tampilkan
          </Typography>
          <Select size="small" value={pageSize} onChange={handlePageSizeChange}>
            {PAGE_SIZE_OPTIONS.map((opt) => (
              <MenuItem key={opt} value={opt}>
                {opt}
              </MenuItem>
            ))}
          </Select>
          <Typography variant="body2" color="text.secondary">
            data
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          width: "100%",
          overflowX: "auto",
          border: `1px solid ${BORDER_COLOR}`,
          borderRadius: 2,
        }}
      >
        <Table
          sx={{
            minWidth: 900,
            "& .MuiTableCell-root": {
              whiteSpace: "normal",
              overflowWrap: "break-word",
              lineHeight: 1.5,
              borderBottom: `1px solid ${BORDER_COLOR}`,
            },
            "& .MuiTableCell-head": {
              backgroundColor: "#fafafa",
              color: "#666",
              fontSize: "0.75rem",
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: "0.05em",
            },
            "& .MuiTableBody-root .MuiTableRow-root:hover": {
              backgroundColor: "rgba(0, 49, 88, 0.04)",
            },
            "& .MuiTableBody-root .MuiTableRow-root:last-of-type .MuiTableCell-root": {
              borderBottom: "none",
            },
          }}
        >
          <TableHead>
            <TableRow>
              {columns.map((col, index) => (
                <TableCell
                  key={`${col.field}-${index}`}
                  sx={{ width: col.width, maxWidth: col.width || DEFAULT_COL_MAX_WIDTH }}
                >
                  {col.headerName}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              Array.from({ length: skeletonRowCount }).map((_, rowIndex) => (
                <TableRow key={`skeleton-${rowIndex}`}>
                  {columns.map((col, index) => (
                    <TableCell key={`${col.field}-${index}`}>
                      <Skeleton />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : pagedRows.length === 0 ? null : (
              pagedRows.map((row, rowIndex) => (
                <TableRow key={row.id ?? rowIndex}>
                  {columns.map((col, index) => (
                    <TableCell
                      key={`${col.field}-${index}`}
                      sx={{ width: col.width, maxWidth: col.width || DEFAULT_COL_MAX_WIDTH }}
                    >
                      {col.renderCell
                        ? col.renderCell({ value: row[col.field], row })
                        : String(row[col.field] ?? "")}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>

        {!loading && pagedRows.length === 0 && (
          <Box sx={{ py: 5, textAlign: "center", color: "text.secondary" }}>
            Tidak ada data
          </Box>
        )}
      </Box>

      {/* Footer: showing range + pagination */}
      <Box
        sx={{
          mt: 2,
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          {totalCount > 0
            ? `Menampilkan ${pageOffset + 1}-${Math.min(
                pageOffset + (pageSize === "All" ? totalCount : Number(pageSize)),
                totalCount
              )} dari ${totalCount} data`
            : null}
        </Typography>

        {pageSize !== "All" && totalPages > 1 && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Typography variant="body2" color="text.secondary">
              Halaman {currentPage} dari {totalPages}
            </Typography>
            <Button
              size="small"
              variant="outlined"
              disabled={currentPage <= 1}
              onClick={() => goToPage(currentPage - 1)}
            >
              Prev
            </Button>
            <Button
              size="small"
              variant="outlined"
              disabled={currentPage >= totalPages}
              onClick={() => goToPage(currentPage + 1)}
            >
              Next
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default CustomDataGrid;
